import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useApi, errorText } from "@/lib/api";
import { downloadBlob } from "@/lib/csv";
import ImportPreview from "@/components/import/ImportPreview";

const sum = (o) => Object.values(o).reduce((a, b) => a + b, 0);

const ConfirmImport = ({ preview, onConfirm, busy }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild><Button disabled={busy} data-testid="import-confirm-button">{busy ? "Importing…" : "Confirm import"}</Button></AlertDialogTrigger>
    <AlertDialogContent className="bg-card">
      <AlertDialogHeader>
        <AlertDialogTitle>Import {preview.filename}?</AlertDialogTitle>
        <AlertDialogDescription>
          This removes {sum(preview.will_remove.test_data)} test-data rows and {sum(preview.will_remove.previous_import)} rows from any
          earlier import, then saves the workbook's rows. Rows you typed into the app are kept. Every new row is in the audit log.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="import-confirm-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} data-testid="import-confirm-yes">Import</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function ImportExportPage() {
  const api = useApi();
  const navigate = useNavigate();
  const input = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      setPreview(await api("post", "/import/preview", form));
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
    if (input.current) input.current.value = "";
  };
  const commit = async () => {
    setBusy(true);
    try {
      const res = await api("post", `/import/${preview.session_id}/commit`);
      toast.success(`Imported ${res.inserted.transactions} ledger rows and ${res.inserted.director_entries} director entries`);
      setPreview(null);
      navigate("/data-quality");
    } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const cancel = async () => {
    await api("delete", `/import/${preview.session_id}`).catch(() => {});
    setPreview(null);
  };
  const exportXlsx = async () => {
    try {
      const blob = await api("get", "/export/workbook", undefined, { responseType: "blob" });
      downloadBlob(`Financial Records export ${new Date().toISOString().slice(0, 10)}.xlsx`, blob);
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="rise">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="import-title">Import &amp; export</h1>
      <p className="mt-3 text-muted-foreground text-base md:text-lg">Bring in your Excel workbook, or export the app back to the same layout to compare.</p>
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <FileUp className="h-5 w-5 text-emerald-400" />
          <h3 className="font-display text-xl font-semibold mt-3">Import workbook</h3>
          <p className="text-sm text-muted-foreground mt-2">Reads the MONTHLY SHEET tabs, the personal ledger, Table14, Table29, the forecast tables and memo cells. You see a preview first and nothing is saved until you confirm.</p>
          <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
          <Button className="mt-5" disabled={busy} onClick={() => input.current?.click()} data-testid="import-choose-button">{busy && !preview ? "Reading…" : "Choose .xlsx file"}</Button>
        </div>
        <div className="border border-border bg-card p-6">
          <FileDown className="h-5 w-5 text-emerald-400" />
          <h3 className="font-display text-xl font-semibold mt-3">Export to Excel</h3>
          <p className="text-sm text-muted-foreground mt-2">One MONTHLY SHEET per year (Date, Month, Description, Payment recived, Expenditure, Category, Notes) with the monthly, category and forecast tables, plus the personal sheet and Support Sheet. Values only, no formulas.</p>
          <Button variant="outline" className="mt-5" onClick={exportXlsx} data-testid="export-workbook-button">Download .xlsx</Button>
        </div>
      </div>
      {preview && (
        <div className="mt-10">
          <div className="flex flex-wrap items-center gap-3 border border-amber-500/40 bg-amber-500/5 p-4" data-testid="import-preview-bar">
            <span className="text-sm">Preview of <span className="font-mono">{preview.filename}</span>. Nothing has been saved yet.</span>
            <Button variant="ghost" className="ml-auto" onClick={cancel} data-testid="import-cancel-button">Cancel</Button>
            <ConfirmImport preview={preview} onConfirm={commit} busy={busy} />
          </div>
          <ImportPreview preview={preview} />
        </div>
      )}
    </div>
  );
}
